import { useState, createContext, useContext, ReactNode } from 'react'
import { useSnackbar } from 'notistack'
import lodash from 'lodash'
import { useCustomer } from 'services/queries/use-customer'
import { useUpdateCustomer } from 'services/queries/use-update-customer'
import useDefaults from 'utils/use-defaults'

interface User {
  userData?: {
    id?: string | number
    email?: string
    name?: string
  }
  apiUserData?: unknown
}

interface CustomerData {
  [key: string]: unknown
}

interface CustomerStatusContextValue {
  data: CustomerData | undefined
  status: string
  currentCompanyUsers: User[]
  startSavingProcedure: () => void
  completeSavingProcedure: () => Promise<void>
  cancelSavingProcedure: () => void
  isEditing: boolean
  changeField: (field: string, newValue: unknown) => void
  removeRestriction: (field: string) => void
  deleteField: (field: string) => void
}

const ThemeContext = createContext<CustomerStatusContextValue | undefined>(undefined)

const useCompanyStatusContext = (): CustomerStatusContextValue => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useCompanyStatusContext must be used within CustomerStatusProvider')
  }
  return context
}

interface CustomerStatusProviderProps {
  children: ReactNode
  id: string | number
  users: User[]
}

const CustomerStatusProvider = ({ children, id, users }: CustomerStatusProviderProps) => {
  const [newData, setNewData] = useState<CustomerData | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const { getDefaultPlan } = useDefaults()
  const defaultPlan = getDefaultPlan()

  const { enqueueSnackbar } = useSnackbar()
  const { data, status } = useCustomer(id)
  const { mutate } = useUpdateCustomer()

  const startSavingProcedure = () => {
    setIsEditing(true)
  }

  const completeSavingProcedure = async () => {
    setIsEditing(false)
    if (newData && Object.keys(newData).length) {
      mutate({ companyId: String(id), reqBody: { data: newData } })
      setNewData(null)
    }
  }

  const cancelSavingProcedure = () => {
    setNewData(null)
    setIsEditing(false)
  }

  const deleteField = (field: string) => {
    setNewData((currentData) => {
      if (currentData) {
        const updatedData = { ...currentData }
        delete updatedData[field]
        return updatedData
      }
      return currentData
    })
  }

  const changeField = (field: string, newValue: unknown) => {
    if (newValue === null) {
      enqueueSnackbar(
        'You just changed field to null which is not appropriate value!',
        { variant: 'error' }
      )
      return
    }
    if (lodash.isEqual(newValue, defaultPlan[field])) {
      deleteField(field)
      return
    }
    setNewData((currentData) => {
      return { ...currentData, [field]: newValue }
    })
  }

  const removeRestriction = (field: string) => {
    setNewData((currentData) => {
      return { ...currentData, [field]: null }
    })
  }

  return (
    <ThemeContext.Provider
      value={{
        data,
        status,
        currentCompanyUsers: users,
        startSavingProcedure,
        completeSavingProcedure,
        cancelSavingProcedure,
        isEditing,
        changeField,
        removeRestriction,
        deleteField,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export { CustomerStatusProvider, useCompanyStatusContext }
